/**
 * Auth Store - Central session and authentication manager.
 * Handles wallet login, OAuth login, 2FA, token lifecycle, and session persistence.
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { jwtDecode } from "jwt-decode";
import type { Account, LoginResponse } from "@mintmatrix/core";
import * as authApi from "@/services/api/auth";

const ACCESS_TOKEN_KEY = "mm_access_token";
const REFRESH_TOKEN_KEY = "mm_refresh_token";
const ACCOUNT_KEY = "mm_account";

interface JwtPayload {
  sub: string;
  pkh: string | null;
  email: string | null;
  is_custodial: boolean;
  exp: number;
  iat: number;
}

export const useAuthStore = defineStore("auth", () => {
  // -- State --
  const account = ref<Account | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const isAuthenticated = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const requires2FA = ref(false);
  const tempToken = ref<string | null>(null);
  const requires2FASetup = ref(false);
  const isInitialized = ref(false);

  // Promise the router can await before checking auth guards
  let _initResolve: (() => void) | null = null;
  const initPromise = new Promise<void>((resolve) => {
    _initResolve = resolve;
  });

  // -- Computed --
  const accountId = computed(() => account.value?.id || null);
  const isCustodial = computed(() => account.value?.is_custodial ?? false);
  const displayName = computed(() => {
    if (account.value?.alias) return account.value.alias;
    if (account.value?.email) return account.value.email;
    if (account.value?.pkh) {
      return `${account.value.pkh.slice(0, 8)}...${account.value.pkh.slice(-6)}`;
    }
    return "Unknown";
  });

  // -- Token Persistence --
  function persistTokens() {
    if (accessToken.value)
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken.value);
    if (refreshToken.value)
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken.value);
    if (account.value)
      localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account.value));
  }

  function loadPersistedTokens() {
    accessToken.value = localStorage.getItem(ACCESS_TOKEN_KEY);
    refreshToken.value = localStorage.getItem(REFRESH_TOKEN_KEY);
    const savedAccount = localStorage.getItem(ACCOUNT_KEY);
    if (savedAccount) {
      try {
        account.value = JSON.parse(savedAccount);
      } catch {
        account.value = null;
      }
    }
  }

  function clearPersistedTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
  }

  function clearSessionState() {
    cancelTokenRefresh();
    account.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    isAuthenticated.value = false;
    requires2FA.value = false;
    tempToken.value = null;
    requires2FASetup.value = false;
    clearPersistedTokens();
  }

  // -- Auto-refresh Timer --
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleTokenRefresh() {
    cancelTokenRefresh();
    if (!accessToken.value) return;

    try {
      const decoded = jwtDecode<JwtPayload>(accessToken.value);
      const expiresAt = decoded.exp * 1000;
      const refreshAt = expiresAt - 60_000; // Refresh 60s before expiry
      const delay = refreshAt - Date.now();

      if (delay > 0) {
        refreshTimer = setTimeout(async () => {
          try {
            await refreshTokens();
          } catch (refreshError) {
            await handleRefreshFailure(refreshError);
          }
        }, delay);
      } else {
        // Token is about to expire or already expired, refresh now
        refreshTokens().catch((refreshError) => {
          void handleRefreshFailure(refreshError);
        });
      }
    } catch {
      // Can't decode token - ignore
    }
  }

  function cancelTokenRefresh() {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }

  function isNetworkErrorMessage(message: string): boolean {
    return message.includes("Unable to reach the server");
  }

  function isAuthRefreshSessionError(message: string): boolean {
    const normalized = message.toLowerCase();
    return (
      normalized.includes("auth refresh token") ||
      normalized.includes("refresh session") ||
      normalized.includes("relogin required") ||
      normalized.includes("session not found") ||
      normalized.includes("session expired") ||
      normalized.includes("reuse detected")
    );
  }

  function getRefreshFailureMessage(message: string): string {
    if (isAuthRefreshSessionError(message)) {
      return "Your saved auth refresh session is no longer valid. Retry Sign In to issue a new auth session.";
    }

    return message || "Your saved session expired. Please sign in again.";
  }

  async function tryWalletReauthentication() {
    if (!account.value || account.value.is_custodial) return false;

    try {
      const { useWalletStore } = await import("./wallet");
      const walletStore = useWalletStore();
      const recovered = await walletStore.reauthenticate();
      if (recovered) {
        error.value = null;
        return true;
      }
    } catch (reauthError) {
      console.warn("Wallet reauthentication attempt failed:", reauthError);
    }

    return false;
  }

  async function handleRefreshFailure(cause: unknown) {
    const message = cause instanceof Error ? cause.message : "";

    if (isNetworkErrorMessage(message)) {
      console.warn(
        "Backend unreachable during token refresh. Keeping persisted session.",
      );
      scheduleTokenRefresh();
      return true;
    }

    if (isAuthRefreshSessionError(message)) {
      const recovered = await tryWalletReauthentication();
      if (recovered) {
        return true;
      }
    }

    if (isAccessTokenValid()) {
      console.warn(
        "Refresh failed but access token still valid. Keeping session.",
      );
      scheduleTokenRefresh();
      return true;
    }

    error.value = getRefreshFailureMessage(message);
    clearSessionState();
    return false;
  }

  // -- Multi-Tab Sync --
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key === REFRESH_TOKEN_KEY && !event.newValue) {
        // Logged out in another tab
        account.value = null;
        accessToken.value = null;
        refreshToken.value = null;
        isAuthenticated.value = false;
        cancelTokenRefresh();
      }
    });
  }

  // -- Auth Actions --

  function handleLoginResponse(response: LoginResponse) {
    if (response.requires_2fa) {
      requires2FA.value = true;
      tempToken.value = response.tempToken || null;
      return;
    }

    if (response.accessToken && response.refreshToken && response.account) {
      accessToken.value = response.accessToken;
      refreshToken.value = response.refreshToken;
      account.value = response.account;
      isAuthenticated.value = true;
      requires2FA.value = false;
      tempToken.value = null;
      requires2FASetup.value = response.requires_2fa_setup || false;
      persistTokens();
      scheduleTokenRefresh();
    }
  }

  /**
   * Login with wallet: challenge -> sign -> verify -> JWT pair.
   * The wallet API and address/PKH must be provided by the caller (wallet store).
   */
  async function loginWithWallet(
    walletApi: any,
    address: string,
    pkh: string,
    walletVendor?: string,
    networkId: number = 0,
    stakingKeyHash?: string,
  ) {
    isLoading.value = true;
    error.value = null;
    requires2FA.value = false;

    try {
      // 1. Request challenge nonce
      const challenge = await authApi.requestChallenge(address, pkh);

      // 2. Sign the nonce with the wallet (convert UTF-8 string to hex for CIP-30 signData)
      const nonceBytes = new TextEncoder().encode(challenge.nonce);
      const nonceHex = Array.from(nonceBytes, (b) =>
        b.toString(16).padStart(2, "0"),
      ).join("");
      const signature = await walletApi.signData(address, nonceHex);

      // 3. Submit to server
      const response = await authApi.loginWallet({
        nonce: challenge.nonce,
        pkh,
        address,
        signature: { signature: signature.signature, key: signature.key },
        walletVendor,
        networkId,
        stakingKeyHash,
      });

      handleLoginResponse(response);
    } catch (e) {
      error.value = e instanceof Error ? e.message : "Wallet login failed";
      throw e;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Login with OAuth (Google/Apple).
   */
  async function loginWithOAuth(
    provider: "google" | "apple",
    idToken: string,
    networkId?: number,
  ) {
    isLoading.value = true;
    error.value = null;
    requires2FA.value = false;

    try {
      const response = await authApi.loginOAuth({
        provider,
        idToken,
        networkId,
      });
      handleLoginResponse(response);
    } catch (e) {
      error.value = e instanceof Error ? e.message : "OAuth login failed";
      throw e;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Complete 2FA verification.
   */
  async function verify2FA(code: string) {
    if (!tempToken.value) throw new Error("No temp token - start login first");

    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.login2FA(tempToken.value, code);
      handleLoginResponse(response);
    } catch (e) {
      error.value = e instanceof Error ? e.message : "2FA verification failed";
      throw e;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Refresh tokens.
   */
  async function refreshTokens() {
    if (!refreshToken.value) throw new Error("No refresh token");

    const tokens = await authApi.refreshTokens(refreshToken.value);
    accessToken.value = tokens.accessToken;
    refreshToken.value = tokens.refreshToken;
    error.value = null;
    persistTokens();
    scheduleTokenRefresh();
  }

  /**
   * Logout.
   */
  async function logout() {
    cancelTokenRefresh();

    try {
      if (accessToken.value) {
        await authApi.logout(
          accessToken.value,
          undefined,
          refreshToken.value || undefined,
        );
      }
    } catch {
      // Ignore logout API errors
    }

    error.value = null;
    clearSessionState();
  }

  /**
   * Check if the persisted access token JWT is still valid (not expired).
   */
  function isAccessTokenValid(): boolean {
    if (!accessToken.value) return false;
    try {
      const decoded = jwtDecode<JwtPayload>(accessToken.value);
      // Valid if at least 30 seconds remain
      return decoded.exp * 1000 > Date.now() + 30_000;
    } catch {
      return false;
    }
  }

  /**
   * Initialize: restore persisted session on app load.
   *
   * Strategy:
   * 1. Load tokens from localStorage synchronously.
   * 2. If we have persisted credentials, mark authenticated OPTIMISTICALLY.
   * 3. Resolve initPromise IMMEDIATELY so the router guard doesn't block.
   * 4. Validate the session with the server in the BACKGROUND.
   * 5. If the background refresh fails AND the access token is expired,
   *    THEN log the user out. If the access token is still valid,
   *    keep the session alive until it expires naturally.
   */
  async function initialize() {
    loadPersistedTokens();

    // Optimistic auth: if we have persisted tokens + account, trust them
    if (refreshToken.value && account.value) {
      isAuthenticated.value = true;
    }

    // ── Unblock the router IMMEDIATELY ──
    // The guard can now proceed with the optimistic auth state.
    // The background refresh below will correct it if invalid.
    isInitialized.value = true;
    _initResolve?.();

    // ── Background validation ──
    if (refreshToken.value) {
      try {
        await refreshTokens();
        // Refresh succeeded — tokens rotated, schedule next refresh
      } catch (e) {
        await handleRefreshFailure(e);
      }
    }
  }

  /**
   * Get the current access token (for API calls).
   */
  function getAccessToken(): string | null {
    return accessToken.value;
  }

  return {
    // State
    account,
    accessToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    error,
    requires2FA,
    tempToken,
    requires2FASetup,
    isInitialized,
    initPromise,
    // Computed
    accountId,
    isCustodial,
    displayName,
    // Actions
    loginWithWallet,
    loginWithOAuth,
    verify2FA,
    refreshTokens,
    logout,
    initialize,
    getAccessToken,
  };
});
